import { EChartsOption } from 'echarts';
import { getLegendWidth } from './legendHelpers';
import { getIsGoalValue } from './goalValue';

type GridSettingsParams = {
    isExportModeActivated: boolean;
    legendExtraSpace: number;
    verticalAxisWidth: 'sm' | 'md' | 'lg' | undefined;
    isDataZoomShown: boolean;
    showLegend?: boolean;
    showLegendOnRightSide?: boolean;
    useGradientColor?: boolean;
    goalValue?: number;
    width: number;
    isHorizontal?: boolean;
};

function calcNumberSpaceFromLength(value: number, maxValue: number) {
    // We need to calculate the correct right space, in order to show all the numbers,
    // but also make sure that the euro sign doesn't move on the right
    // A char width is between 7 and 8, and we need to add an additional space of 4 for the hundreds, thousands...
    // Finally, we add 20 for the euro sign
    const valLength = value.toString().length;
    if (valLength * 8 + Math.floor((valLength - 1) / 3) * 4 + 20 > maxValue) {
        return maxValue;
    }
    return valLength * 8 + Math.floor((valLength - 1) / 3) * 4 + 20;
}

/**
 * Get the eCharts settings for the grid.
 * @param {GridSettingsParams} params - the parameters to build the grid settings
 * @returns {EChartsOption['grid']} the grid settings
 * @see https://echarts.apache.org/en/option.html#grid
 */

export const getGridSettings = ({
    isExportModeActivated,
    legendExtraSpace,
    isDataZoomShown,
    showLegend,
    showLegendOnRightSide,
    useGradientColor,
    goalValue,
    width,
    isHorizontal,
}: GridSettingsParams): EChartsOption['grid'] => {
    const baseGridSettings = {
        right: (() => {
            if (!getIsGoalValue(goalValue) || !goalValue || isHorizontal) {
                return 20;
            }
            return calcNumberSpaceFromLength(goalValue, 150);
        })(),
        left: 20,
        top: 40,
        bottom: isDataZoomShown ? 80 : 50,
        containLabel: true,
    };

    const getGridSettingsForGradientColorUse = () =>
        showLegend && showLegendOnRightSide
            ? {
                  right: 120,
              }
            : {
                  top: 60,
              };
    const getGridSettingsForClassicLegend = () =>
        showLegend && showLegendOnRightSide
            ? {
                  right: (() => {
                      if (
                          !getIsGoalValue(goalValue) ||
                          !goalValue ||
                          isHorizontal
                      ) {
                          return 50 + getLegendWidth(width);
                      }
                      return (
                          calcNumberSpaceFromLength(goalValue, 150) +
                          getLegendWidth(width)
                      );
                  })(),
                  bottom: 60,
              }
            : {
                  top:
                      40 +
                      (showLegend ? 20 : 0) +
                      (isExportModeActivated ? legendExtraSpace : 0),
              };

    return useGradientColor
        ? { ...baseGridSettings, ...getGridSettingsForGradientColorUse() }
        : { ...baseGridSettings, ...getGridSettingsForClassicLegend() };
};
